import type { Html, HtmlBuilder } from "foldkit/html";
import { type Token, type Tokens, lexer } from "marked";
import { namedInteractions } from "../app/namedInteractions";

import { safeUrl } from "./safeUrl";

/**
 * Markdown preview (KTD6). `marked` (framework-agnostic, no React peer)
 * tokenizes the source; this module renders the tokens straight to Foldkit
 * VRecords. There is no HTML string and no `innerHTML` anywhere, so the
 * allowlist is the renderer itself: a token type not handled below renders
 * as nothing. Raw HTML is displayed as inert code, inspired by Girginakku
 * raw_block/raw_inline nodes, and never parsed into elements.
 *
 * v1 feature contract (`@uiw/react-markdown-preview` with GFM, as used by
 * `Entry.tsx:710-716`): headings, paragraphs, emphasis/strong/strikethrough,
 * inline code and fenced code blocks (no syntax highlighting here), links,
 * images, ordered/unordered lists, task-list checkboxes (read-only),
 * blockquotes, tables (with alignment), horizontal rules, line breaks.
 * Not ported: live raw HTML (v1 rendered it through rehype),
 * heading anchor links, code highlighting, footnotes.
 *
 * URL policy: `href` and `src` survive only when `http:`, `https:`, or
 * relative (no scheme). `javascript:`, `data:`, `vbscript:` and every other
 * scheme and malformed URLs are removed (links become text, images their alt).
 * The shared URL parser policy accounts for browser control-character handling.
 */

const ENTITIES: Record<string, string> = {
	"&amp;": "&",
	"&lt;": "<",
	"&gt;": ">",
	"&quot;": '"',
	"&#39;": "'",
};
const decodeEntities = (text: string): string =>
	text.replace(
		/&(amp|lt|gt|quot|#39);/g,
		(entity) => ENTITIES[entity] ?? entity,
	);

/** Renders a plain text run; pages hook it to turn `[[wiki links]]` into elements. */
export type TextRenderer = (text: string) => ReadonlyArray<Html | string>;
const plain: TextRenderer = (text) => [text];

const inline = <Message>(
	h: HtmlBuilder<Message>,
	text: TextRenderer,
	tokens: ReadonlyArray<Token> | undefined,
): ReadonlyArray<Html | string> =>
	(tokens ?? []).flatMap((token) => render(h, text, token));

const render = <Message>(
	h: HtmlBuilder<Message>,
	text: TextRenderer,
	token: Token,
): ReadonlyArray<Html | string> => {
	switch (token.type) {
		case "space":
		case "def":
		case "tag":
			return [];
		case "html": {
			const t = token as Tokens.HTML;
			const code = h.code([], [t.text]);
			return [t.block ? h.pre([], [code]) : code];
		}
		case "heading": {
			const t = token as Tokens.Heading;
			const tag = (["h1", "h2", "h3", "h4", "h5", "h6"] as const)[
				Math.min(Math.max(t.depth, 1), 6) - 1
			];
			return [h[tag ?? "h6"]([], inline(h, text, t.tokens))];
		}
		case "paragraph":
			return [h.p([], inline(h, text, (token as Tokens.Paragraph).tokens))];
		case "text": {
			const t = token as Tokens.Text;
			return t.tokens
				? inline(h, text, t.tokens)
				: text(decodeEntities(t.text));
		}
		case "escape":
			return [decodeEntities((token as Tokens.Escape).text)];
		case "strong":
			return [h.strong([], inline(h, text, (token as Tokens.Strong).tokens))];
		case "em":
			return [h.em([], inline(h, text, (token as Tokens.Em).tokens))];
		case "del":
			return [h.del([], inline(h, text, (token as Tokens.Del).tokens))];
		case "codespan":
			return [h.code([], [decodeEntities((token as Tokens.Codespan).text)])];
		case "code":
			return [h.pre([], [h.code([], [(token as Tokens.Code).text])])];
		case "br":
			return [h.br([])];
		case "hr":
			return [h.hr([])];
		case "blockquote":
			return [
				h.blockquote([], inline(h, text, (token as Tokens.Blockquote).tokens)),
			];
		case "link": {
			const t = token as Tokens.Link;
			const href = safeUrl(t.href);
			return href === undefined
				? inline(h, text, t.tokens)
				: [
						h.a(
							[
								h.Href(href),
								...(t.title ? [h.Title(t.title)] : []),
								h.Target("_blank"),
								h.DataAttribute(
									"analytics-event",
									namedInteractions.markdownLink,
								),
								h.Rel("noopener noreferrer"),
							],
							inline(h, plain, t.tokens),
						),
					];
		}
		case "image": {
			const t = token as Tokens.Image;
			const src = safeUrl(t.href);
			return src === undefined
				? [decodeEntities(t.text)]
				: [h.img([h.Src(src), h.Alt(decodeEntities(t.text))])];
		}
		case "list": {
			const t = token as Tokens.List;
			const items = t.items.map((item) =>
				h.li(
					[],
					[
						...(item.task
							? [
									h.input([
										h.Type("checkbox"),
										h.Checked(item.checked === true),
										h.Disabled(true),
									]),
									" ",
								]
							: []),
						...inline(
							h,
							text,
							item.tokens.filter((child) => child.type !== "checkbox"),
						),
					],
				),
			);
			return [t.ordered ? h.ol([], items) : h.ul([], items)];
		}
		case "table": {
			const t = token as Tokens.Table;
			const cell = (c: Tokens.TableCell, tag: "th" | "td") =>
				h[tag](
					c.align ? [h.Class(`text-${c.align}`)] : [],
					inline(h, text, c.tokens),
				);
			return [
				h.table(
					[h.Class("table")],
					[
						h.thead(
							[],
							[
								h.tr(
									[],
									t.header.map((c) => cell(c, "th")),
								),
							],
						),
						h.tbody(
							[],
							t.rows.map((row) =>
								h.tr(
									[],
									row.map((c) => cell(c, "td")),
								),
							),
						),
					],
				),
			];
		}
		default:
			return [];
	}
};

/** Renders GFM markdown to VRecords; nothing outside the allowlist above can appear. */
export const markdown = <Message>(
	h: HtmlBuilder<Message>,
	source: string,
	text: TextRenderer = plain,
): Html =>
	h.div(
		[h.Class("prose max-w-none")],
		lexer(source, { gfm: true }).flatMap((token) => render(h, text, token)),
	);
